import React from 'react';
import styled from 'styled-components';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Spacing, Palette } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { Text } from '@/shared/ui';
import { Checkbox, RadioButton } from '@/shared/inputs';

const AlwaysVisibleContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const DescriptionContainer = styled.div`
  padding-left: ${Spacing.x4 + Spacing.x1_5}px;
  padding-right: ${Spacing.x3 + Spacing.x1_5}px;
  margin-top: ${Spacing.x0_5}px;
`;

const Description = styled(Text)`
  color: ${BorisScreener.inkSoft};
  font-size: 13.5px;
`;

const Title = styled(Text)<{ disabled?: boolean; titleAlign?: 'start' | 'end' }>`
  flex: 1;
  color: ${({ disabled }) => (disabled ? Palette.natural300 : Palette.dark)};
  align-self: center;
  line-height: ${Spacing.x4}px;
  font-size: 17px;
  font-weight: 500;
  ${({ titleAlign }) => titleAlign === 'end' && `padding-left: ${Spacing.x1_5}px;`}
`;

const IconWrapper = styled.div`
  align-self: center;
  margin-right: ${Spacing.x1_5}px;
`;

export type SelectionCardProps = {
  value: string;
  selected: boolean;
  onClick: (value: string) => void;
  title?: string;
  description?: string;
  IconComponent?: React.ReactNode;
  disabled?: boolean;
  shouldShowDescription?: boolean;
  type?: 'checkbox' | 'radio';
  titleAlign?: 'start' | 'end';
  /** Boris `.c-checkopt.dashed` — dashed border for the opt-out ("None of these") card. */
  dashed?: boolean;
};

const SelectionCard: React.FC<SelectionCardProps> = ({
  value,
  selected,
  onClick,
  title,
  description,
  IconComponent,
  disabled,
  shouldShowDescription = false,
  type = 'checkbox',
  titleAlign = 'start',
  dashed = false,
}) => {
  const handleClick = () => {
    if (!disabled) {
      onClick(value);
    }
  };

  const SelectInput = type === 'checkbox' ? Checkbox : RadioButton;

  const TitleElement = title ? (
    <Title size='p1' bold={selected} disabled={disabled} titleAlign={titleAlign} aria-hidden="true">
      {title}
    </Title>
  ) : null;

  return (
    <Card
      onClick={handleClick}
      sx={{
        boxShadow: 'none',
        // Boris `.c-checkopt.on` selects with an ink border (no width jump), not coral.
        border: selected
          ? `1.5px solid ${BorisScreener.ink}`
          : `1.5px solid ${BorisScreener.line}`,
        borderStyle: dashed ? 'dashed' : 'solid',
        borderRadius: '16px',
        // #FCFBF0 is Boris's `.c-checkopt.on` selected surface (ds-controls.css);
        // no design token exists for it yet, so it's hardcoded here.
        backgroundColor: selected ? '#FCFBF0' : '#fff',
        marginBottom: `${Spacing.x1}px`,
        opacity: disabled ? '0.6' : '1',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          borderColor: selected ? BorisScreener.ink : BorisScreener.lineStrong,
        },
      }}
      aria-label={selected ? title : `${title}. ${description}`}
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
    >
      <CardContent sx={{ padding: '16px 18px', '&:last-child': { paddingBottom: '16px' } }}>
        <AlwaysVisibleContent>
          {IconComponent && <IconWrapper>{IconComponent}</IconWrapper>}
          {titleAlign === 'start' && TitleElement}
          <SelectInput
            checked={selected}
            disabled={disabled}
            inputProps={{
              'aria-label': selected ? title : `${title}. ${description}`,
              'aria-checked': selected,
              'aria-disabled': disabled,
            }}
          />
          {titleAlign === 'end' && TitleElement}
        </AlwaysVisibleContent>
        {shouldShowDescription && description && (
          <DescriptionContainer>
            <Description size="p2">{description}</Description>
          </DescriptionContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default SelectionCard;
